// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import constants from "@store/constants";
import setFieldValue from "@store/Framework/Store/Greif/Components/RecommendedProductRequest/Handlers/SetFieldValue";
import { RecommendedProductRequestFormContext } from "@store/Library/Widgets/RecommendedProductRequest/RecommendedProductRequestForm";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    fieldLabel = "fieldLabel",
    fieldLabelInstruction = "fieldLabelInstruction",
    isRequired = "isRequired",
    isDisabled = "isDisabled",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isRequired]: boolean;
        [fields.isDisabled]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    phoneState: state.greif.components.recommendedProductRequestForm.fieldValues["phone"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PhoneStyles {
    phoneTextField?: TextFieldPresentationProps;
    phoneLabelAdditionalText?: TypographyPresentationProps;
}

export const phoneStyles: PhoneStyles = {
    phoneLabelAdditionalText: {
        css: css`
            margin-left: ${pxToRem(8)};
            font-weight: ${x => x.theme.design.typography.weight.base};
        `,
    },
};

const styles = phoneStyles;

const formatPhoneNumber = (phone: string) => {
    const phoneTest = new RegExp(/^((\+1)|1)? ?\(?(\d{3})\)?[ .-]?(\d{3})[ .-]?(\d{4})( ?(ext\.? ?|x)(\d*))?$/);
    phone = phone.trim();
    const results = phoneTest.exec(phone);

    if (results !== null && results.length > 8) {
        return `(${results[3]}) ${results[4]}-${results[5]}${
            typeof results[8] !== "undefined" ? ` x${results[8]}` : ""
        }`;
    }

    return phone;
};

const Phone: FC<Props> = ({ fields, phoneState, setFieldValue }) => {
    const { isRequired, isDisabled } = fields;
    const recommendedProductRequestFormContext = useContext(RecommendedProductRequestFormContext);
    const [phoneNumberValue, setPhoneNumberValue] = useState(phoneState || "");
    const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
    const [phone, SetPhone] = useState("");

    const phonePlaceholder = translate("(###) ###-####");
    const phoneLabel = translate("Phone Number");
    const phoneInstructionLabel = translate("(for preferred contact via phone)");

    useEffect(() => () => setFieldValue({ key: "phone", value: undefined }), []);

    useEffect(() => {
        recommendedProductRequestFormContext.validators.phone = validatePhone;
    }, [phone]);

    useEffect(() => {
        recommendedProductRequestFormContext.validators.phone = validatePhone;
    }, [isRequired]);

    const validatePhone = () => {
        const errorMessage = isRequired && !phone ? (translate(`${phoneLabel} is required`) as string) : "";
        setPhoneErrorMessage(errorMessage);
        return !errorMessage;
    };

    const phoneChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const reg = new RegExp("^[0-9]+$");
        const formattedPhoneNumber = formatPhoneNumber(event.target.value);

        SetPhone(formattedPhoneNumber);
    };

    const phoneOnBlurHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const formattedPhoneNumber = formatPhoneNumber(event.target.value);
        setPhoneNumberValue(formattedPhoneNumber);
        setFieldValue({ key: "phone", value: phoneNumberValue });
    };

    return (
        <TextField
            {...styles.phoneTextField}
            label={
                <>
                    {phoneLabel}
                    {phoneInstructionLabel && (
                        <Typography {...styles.phoneLabelAdditionalText}>{phoneInstructionLabel}</Typography>
                    )}
                </>
            }
            value={phoneNumberValue || ""}
            onChange={phoneChangeHandler}
            required={isRequired}
            error={phoneErrorMessage}
            disabled={isDisabled}
            placeholder={phonePlaceholder}
            onBlur={phoneOnBlurHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Phone),
    definition: {
        displayName: "Phone",
        group: constants.widgetGroup.recommendedProductRequest,
        icon: "input-pipe",
        allowedContexts: [constants.pageType.recommendedProductRequest],
        fieldDefinitions: [
            {
                name: fields.isDisabled,
                displayName: "Disabled?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
